/* eslint-disable react/prop-types */
import * as React from "react";
import {
  Avatar,
  Badge,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useLocation, useNavigate } from "react-router-dom";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import DeleteSweepOutlinedIcon from "@mui/icons-material/DeleteSweepOutlined";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import LogoutIcon from "@mui/icons-material/Logout";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import secureLocalStorage from "react-secure-storage";
import { getDownloadURL, ref } from "firebase/storage";
import { imageDb } from "../Firebase/Config";
import ImageUpload from "../ImageUpload/UploadImage";
import LogoutDialog from "./LogoutDialog";
import Logo from "../assets/Logo.png";
import { APP_NAME, colors, SIDEBAR_WIDTH } from "../brand";

const text = (v) => String(v ?? "").trim();

export default function SideBar({
  pendingCount = 0,
  deletionCount = 0,
  onNavigate,
}) {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const user = React.useMemo(() => {
    try {
      return JSON.parse(secureLocalStorage.getItem("profile"));
    } catch {
      return null;
    }
  }, []);

  const firstName = text(user?.results?.firstName);
  const lastName = text(user?.results?.lastName);
  const email = text(user?.results?.email);
  const reconRole = text(user?.results?.reconciliation);
  const initials = (
    (firstName[0] || "U") + (lastName[0] || firstName[1] || "")
  ).toUpperCase();

  const [imageUrl, setImageUrl] = React.useState(null);
  const [photoOpen, setPhotoOpen] = React.useState(false);
  const [logoutOpen, setLogoutOpen] = React.useState(false);

  const allowApprove =
    reconRole === "Approver" || reconRole === "Administrator";
  const allowUpdate =
    reconRole === "Administrator" || reconRole === "Read-Write";

  const sections = [
    {
      title: "Overview",
      items: [
        {
          text: "Dashboard",
          to: "/reconciliation/",
          icon: <DashboardOutlinedIcon />,
        },
      ],
    },
    {
      title: "Reconcile",
      items: [
        {
          text: "Two Statements",
          to: "/reconciliation/new-reconciliation",
          icon: <CompareArrowsIcon />,
        },
        {
          text: "Single Statement",
          to: "/reconciliation/single-reconciliation",
          icon: <ReceiptLongOutlinedIcon />,
        },
      ],
    },
    {
      title: "Reports",
      items: [
        {
          text: "Reports",
          to: "/reconciliation/report",
          icon: <AssessmentOutlinedIcon />,
        },
        allowUpdate && {
          text: "Update Report",
          to: "/reconciliation/update-report",
          icon: <EditNoteOutlinedIcon />,
        },
        allowApprove && {
          text: "Approvals",
          to: "/reconciliation/approvals",
          icon: <FactCheckOutlinedIcon />,
          badge: pendingCount,
        },
        (allowUpdate || allowApprove) && {
          text: "Delete Reports",
          to: "/reconciliation/delete-reports",
          icon: <DeleteSweepOutlinedIcon />,
          badge: deletionCount,
        },
      ].filter(Boolean),
    },
    {
      title: "Settings",
      items: [
        {
          text: "Locations",
          to: "/reconciliation/location",
          icon: <PlaceOutlinedIcon />,
        },
        {
          text: "My Account",
          to: "/reconciliation/user-page",
          icon: <ManageAccountsOutlinedIcon />,
        },
      ],
    },
  ];

  // Profile picture from Firebase storage, initials when there is none
  React.useEffect(() => {
    let mounted = true;
    if (!email) return undefined;
    getDownloadURL(ref(imageDb, `profile_images/${email}`))
      .then((url) => mounted && setImageUrl(url))
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, [email]);

  const isActive = (to) =>
    pathname === to ||
    (to === "/reconciliation/" &&
      (pathname === "/" || pathname === "/reconciliation"));

  const go = (to) => {
    navigate(to);
    onNavigate?.();
  };

  return (
    <Box
      component="nav"
      aria-label="Main menu"
      sx={{
        width: SIDEBAR_WIDTH,
        height: "100%",
        backgroundColor: colors.primary,
        color: "#F3F3F4",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Brand */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.2,
          px: 2,
          minHeight: 60,
          borderBottom: `1px solid ${alpha("#ffffff", 0.08)}`,
        }}
      >
        <img src={Logo} alt="" width={32} height={32} style={{ objectFit: "contain" }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800, fontSize: 15, lineHeight: 1.1 }}>
            {APP_NAME}
          </Typography>
          <Typography sx={{ fontSize: 11, opacity: 0.6 }}>CBX Suite</Typography>
        </Box>
      </Box>

      {/* Menu */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          px: 1.25,
          py: 1.5,
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            background: alpha("#ffffff", 0.14),
            borderRadius: 999,
          },
        }}
      >
        {sections.map((section) => (
          <Box key={section.title} sx={{ mb: 1.5 }}>
            <Typography
              sx={{
                px: 1.25,
                pb: 0.5,
                fontSize: 10.5,
                letterSpacing: 1,
                textTransform: "uppercase",
                opacity: 0.55,
              }}
            >
              {section.title}
            </Typography>
            <List disablePadding sx={{ display: "grid", gap: 0.25 }}>
              {section.items.map((item) => {
                const active = isActive(item.to);
                return (
                  <ListItemButton
                    key={item.to}
                    onClick={() => go(item.to)}
                    aria-current={active ? "page" : undefined}
                    sx={{
                      borderRadius: 2,
                      py: 0.8,
                      px: 1,
                      position: "relative",
                      background: active
                        ? alpha(colors.secondary, 0.16)
                        : "transparent",
                      "&:hover": {
                        background: active
                          ? alpha(colors.secondary, 0.22)
                          : alpha("#ffffff", 0.06),
                      },
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        left: 0,
                        top: 8,
                        bottom: 8,
                        width: 3,
                        borderRadius: 999,
                        backgroundColor: active
                          ? colors.secondary
                          : "transparent",
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 36,
                        color: active ? "#fff" : alpha("#ffffff", 0.7),
                      }}
                    >
                      <Badge
                        color="error"
                        badgeContent={item.badge || 0}
                        max={99}
                      >
                        {item.icon}
                      </Badge>
                    </ListItemIcon>
                    <ListItemText
                      primary={item.text}
                      primaryTypographyProps={{
                        fontSize: 13,
                        fontWeight: active ? 800 : 600,
                        color: active ? "#fff" : alpha("#ffffff", 0.86),
                      }}
                    />
                  </ListItemButton>
                );
              })}
            </List>
          </Box>
        ))}
      </Box>

      {/* User card */}
      <Box sx={{ px: 1.5, pb: 1.5, pt: 1 }}>
        <Divider sx={{ mb: 1.5, borderColor: alpha("#ffffff", 0.08) }} />
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 1.2 }}>
          <Badge
            overlap="circular"
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            badgeContent={
              <Tooltip title="Change profile picture">
                <IconButton
                  aria-label="Change profile picture"
                  onClick={() => setPhotoOpen(true)}
                  size="small"
                  sx={{
                    width: 20,
                    height: 20,
                    bgcolor: alpha("#000000", 0.45),
                    border: `1px solid ${alpha("#ffffff", 0.25)}`,
                    "&:hover": { bgcolor: alpha("#000000", 0.6) },
                  }}
                >
                  <EditIcon sx={{ fontSize: 12, color: "#fff" }} />
                </IconButton>
              </Tooltip>
            }
          >
            <Avatar
              alt={`${firstName} ${lastName}`}
              src={imageUrl || undefined}
              sx={{
                width: 40,
                height: 40,
                fontWeight: 800,
                fontSize: 15,
                bgcolor: alpha(colors.secondary, 0.35),
              }}
            >
              {initials}
            </Avatar>
          </Badge>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              noWrap
              sx={{ fontSize: 13, fontWeight: 800, color: "#fff" }}
            >
              {firstName} {lastName}
            </Typography>
            <Chip
              size="small"
              label={reconRole || "User"}
              sx={{
                mt: 0.4,
                height: 20,
                fontSize: 10.5,
                fontWeight: 700,
                color: "#0b1b14",
                bgcolor: alpha(colors.secondary, 0.9),
              }}
            />
          </Box>
        </Box>
        <Button
          fullWidth
          size="small"
          onClick={() => setLogoutOpen(true)}
          startIcon={<LogoutIcon />}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            borderRadius: "10px",
            color: "#fff",
            border: `1px solid ${alpha("#ffffff", 0.18)}`,
            "&:hover": { background: alpha("#ffffff", 0.08) },
          }}
        >
          Log out
        </Button>
      </Box>

      {/* Profile picture */}
      <Dialog
        open={photoOpen}
        onClose={() => setPhotoOpen(false)}
        PaperProps={{ sx: { borderRadius: "16px" } }}
      >
        <DialogTitle sx={{ pr: 6, fontWeight: 800 }}>
          Profile picture
          <IconButton
            aria-label="Close"
            onClick={() => setPhotoOpen(false)}
            sx={{ position: "absolute", right: 10, top: 10 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: colors.textSecondary, mb: 2 }}>
            A picture helps people recognise you and confirms you are signed
            in to the right account.
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
            <ImageUpload />
          </Box>
        </DialogContent>
      </Dialog>

      <LogoutDialog open={logoutOpen} onClose={() => setLogoutOpen(false)} />
    </Box>
  );
}
